import type { ModuleGridType } from 'types/studio/items'

import {
  buildVanDerValkFieldLocationOnRoofPlane,
  computeVanDerValkRoofDownslopeHorizontalUnit,
  type VanDerValkRoofPolygonPoint,
} from './buildVanDerValkPitchedRoofSlope'
import { normalizeVanDerValkGridCells, type VanDerValkNormalizedGridCell } from './vanDerValkNormalizeGridCells'

export type VanDerValkFieldLocationPoint = { x: number; y: number; z: number }

export type VanDerValkFieldLocation = {
  base: VanDerValkFieldLocationPoint
  azimuth: VanDerValkFieldLocationPoint
  tangent: VanDerValkFieldLocationPoint
}

export type VanDerValkBuildFieldLocationErrorReason = 'no_active_layout' | 'invalid_field_location'

export type BuildVanDerValkFieldLocationFromGridResult =
  | { ok: true; location: VanDerValkFieldLocation }
  | { ok: false; reason: VanDerValkBuildFieldLocationErrorReason }

const MM_PER_METRE = 1000
const COORD_MIN_MM = -500_000
const COORD_MAX_MM = 500_000
const COLLINEAR_EPSILON_MM = 1

type StudioPointM = { x: number; y: number; z: number }
type StudioHorizontalDir = { east: number; south: number }

type LayoutParams = {
  moduleWidth: number
  moduleHeight: number
  modulesPerRow: number
  modulesPerCol: number
  totalInterModuleSpaceX: number
  totalInterModuleSpaceY: number
}

type GridLayoutRuntime = ModuleGridType & {
  getParamsForLayoutCalcs?: (opts: {
    ignoreGroundClearance: boolean
    flipOrientation: boolean
    additionalZOffset: number
  }) => LayoutParams
  positionAndRotationForCell?: (params: Record<string, unknown>) => {
    position: { x: number; y: number; z: number; clone?: () => { x: number; y: number; z: number } }
  }
  getCellsActiveVector2Dict?: () => Record<string, { x: number; y: number }>
  localToWorld?: (v: { x: number; y: number; z: number }) => { x: number; y: number; z: number }
  updateMatrixWorld?: () => void
  getAzimuth?: () => number
  getSlope?: () => number
}

/** Studio scene metres → Van der Valk field location mm (X=E–W, Y=height, Z=N–S). */
export function studioMetresToVendorMm(point: StudioPointM): VanDerValkFieldLocationPoint {
  return {
    x: Math.round(point.x * MM_PER_METRE),
    y: Math.round(point.z * MM_PER_METRE),
    z: Math.round(point.y * MM_PER_METRE),
  }
}

/**
 * Vendor azimuth (degrees, clockwise from north in the XZ plane) from ``base`` → ``azimuth`` points.
 * Matches VanderValk: ``radToDeg(-Math.atan2(azimuthDir.x, azimuthDir.z))``.
 */
export function computeVanDerValkAzimuthDegFromLocation(
  base: VanDerValkFieldLocationPoint,
  azimuthPoint: VanDerValkFieldLocationPoint
): number {
  const dx = azimuthPoint.x - base.x
  const dz = azimuthPoint.z - base.z
  const rad = -Math.atan2(dx, dz)
  const deg = (rad * 180) / Math.PI
  return ((deg % 360) + 360) % 360
}

/** Signed vendor azimuth (degrees) without 0–360 normalisation — matches vendor error messages. */
export function computeVanDerValkSignedAzimuthDegFromLocation(
  base: VanDerValkFieldLocationPoint,
  azimuthPoint: VanDerValkFieldLocationPoint
): number {
  const dx = azimuthPoint.x - base.x
  const dz = azimuthPoint.z - base.z
  return (-Math.atan2(dx, dz) * 180) / Math.PI
}

/** Canonical downslope azimuth (signed deg) shared by all fields on a pitched roof polygon. */
export function canonicalVanDerValkSignedAzimuthDegFromRoofPolygon(
  roofPolygon: VanDerValkRoofPolygonPoint[]
): number | null {
  const downslope = computeVanDerValkRoofDownslopeHorizontalUnit(roofPolygon)
  if (!downslope) {
    return null
  }
  return computeVanDerValkSignedAzimuthDegFromLocation(
    { x: 0, y: 0, z: 0 },
    { x: downslope.x, y: 0, z: downslope.z }
  )
}

/**
 * Snap ``azimuth`` onto the roof downslope bearing, preserving horizontal distance.
 * Uses a fixed X/Z ratio from ``downslopeUnit`` (derive ``dx`` from rounded ``dz``).
 */
export function snapVanDerValkLocationAzimuthToRoofDownslope(
  base: VanDerValkFieldLocationPoint,
  azimuth: VanDerValkFieldLocationPoint,
  downslopeUnit: { x: number; z: number }
): VanDerValkFieldLocationPoint {
  const dx0 = azimuth.x - base.x
  const dz0 = azimuth.z - base.z
  const horizontalDist = Math.hypot(dx0, dz0)
  if (horizontalDist < 1) {
    return azimuth
  }
  const unitLen = Math.hypot(downslopeUnit.x, downslopeUnit.z)
  if (unitLen < 1e-12) {
    return azimuth
  }
  const ux = downslopeUnit.x / unitLen
  const uz = downslopeUnit.z / unitLen
  let dz = Math.round(uz * horizontalDist)
  if (dz === 0 && Math.abs(uz) > 1e-9) {
    dz = uz > 0 ? 1 : -1
  }
  const dx =
    Math.abs(uz) > 1e-9 ? Math.round(dz * (ux / uz)) : Math.round(ux * horizontalDist)
  return { x: base.x + dx, y: azimuth.y, z: base.z + dz }
}

/** Horizontal offset ``base → azimuth`` (vendor azimuth uses X/Z only; distance does not matter). */
export function sharedVanDerValkAzimuthHorizontalOffsetFromLocation(
  base: VanDerValkFieldLocationPoint,
  azimuth: VanDerValkFieldLocationPoint
): { dx: number; dz: number } | null {
  const dx = azimuth.x - base.x
  const dz = azimuth.z - base.z
  if (dx === 0 && dz === 0) {
    return null
  }
  return { dx, dz }
}

/** Apply the same horizontal azimuth offset to a field (preserves per-field ``azimuth.y`` for pitch). */
export function applySharedVanDerValkAzimuthHorizontalOffset(
  base: VanDerValkFieldLocationPoint,
  azimuth: VanDerValkFieldLocationPoint,
  shared: { dx: number; dz: number }
): VanDerValkFieldLocationPoint {
  return {
    x: base.x + shared.dx,
    y: azimuth.y,
    z: base.z + shared.dz,
  }
}

/** Nudge azimuth X/Z so every field yields the same signed bearing as ``referenceSignedAzimuthDeg``. */
export function harmonizeVanDerValkFieldAzimuthToSignedBearing(
  base: VanDerValkFieldLocationPoint,
  azimuth: VanDerValkFieldLocationPoint,
  referenceSignedAzimuthDeg: number
): VanDerValkFieldLocationPoint {
  if (computeVanDerValkSignedAzimuthDegFromLocation(base, azimuth) === referenceSignedAzimuthDeg) {
    return azimuth
  }
  const dx0 = azimuth.x - base.x
  const dz0 = azimuth.z - base.z
  const horizontalDist = Math.hypot(dx0, dz0)
  if (horizontalDist < 1) {
    return azimuth
  }
  const targetRad = (-referenceSignedAzimuthDeg * Math.PI) / 180
  const sinT = Math.sin(targetRad)
  const cosT = Math.cos(targetRad)
  let best = azimuth
  let bestErr = Infinity
  for (let dzNudge = -30; dzNudge <= 30; dzNudge++) {
    const dz = Math.round(cosT * horizontalDist) + dzNudge
    if (dz === 0) {
      continue
    }
    const dx = Math.abs(cosT) > 1e-12 ? Math.round(dz * (sinT / cosT)) : Math.round(sinT * horizontalDist)
    const trial = { x: base.x + dx, y: azimuth.y, z: base.z + dz }
    const signed = computeVanDerValkSignedAzimuthDegFromLocation(base, trial)
    if (signed === referenceSignedAzimuthDeg) {
      return trial
    }
    const err = Math.abs(signed - referenceSignedAzimuthDeg)
    if (err < bestErr) {
      bestErr = err
      best = trial
    }
  }
  return best
}

function isFinitePointMm(p: VanDerValkFieldLocationPoint): boolean {
  return Number.isFinite(p.x) && Number.isFinite(p.y) && Number.isFinite(p.z)
}

function isWithinBoundsMm(p: VanDerValkFieldLocationPoint): boolean {
  return (
    p.x >= COORD_MIN_MM &&
    p.x <= COORD_MAX_MM &&
    p.y >= COORD_MIN_MM &&
    p.y <= COORD_MAX_MM &&
    p.z >= COORD_MIN_MM &&
    p.z <= COORD_MAX_MM
  )
}

function crossProductMm(
  base: VanDerValkFieldLocationPoint,
  tangent: VanDerValkFieldLocationPoint,
  azimuth: VanDerValkFieldLocationPoint
): VanDerValkFieldLocationPoint {
  const ux = tangent.x - base.x
  const uy = tangent.y - base.y
  const uz = tangent.z - base.z
  const vx = azimuth.x - base.x
  const vy = azimuth.y - base.y
  const vz = azimuth.z - base.z
  return {
    x: uy * vz - uz * vy,
    y: uz * vx - ux * vz,
    z: ux * vy - uy * vx,
  }
}

function isCollinearMm(
  base: VanDerValkFieldLocationPoint,
  tangent: VanDerValkFieldLocationPoint,
  azimuth: VanDerValkFieldLocationPoint
): boolean {
  const cp = crossProductMm(base, tangent, azimuth)
  const len = Math.hypot(cp.x, cp.y, cp.z)
  return len < COLLINEAR_EPSILON_MM
}

function studioHorizontalSouthEastUnit(azimuthDeg: number): StudioHorizontalDir {
  const rad = (azimuthDeg * Math.PI) / 180
  return { east: Math.sin(rad), south: -Math.cos(rad) }
}

function tangentDirFromAzimuthDir(azimuthDir: StudioHorizontalDir): StudioHorizontalDir {
  return { east: azimuthDir.south, south: -azimuthDir.east }
}

function studioHorizontalDirFromVendorXzUnit(vendorX: number, vendorZ: number): StudioHorizontalDir | null {
  const len = Math.hypot(vendorX, vendorZ)
  if (len < 1e-9) {
    return null
  }
  return { east: vendorX / len, south: vendorZ / len }
}

/** Row-step direction: roof downslope on pitched facets; panel facing azimuth on flat roofs. */
function rowStepDirForFieldLocation(
  azimuthDeg: number,
  slopeDeg: number,
  roofPolygon: VanDerValkRoofPolygonPoint[] | undefined
): StudioHorizontalDir {
  if (slopeDeg > 0 && roofPolygon?.length) {
    const downslope = computeVanDerValkRoofDownslopeHorizontalUnit(roofPolygon)
    if (downslope) {
      const dir = studioHorizontalDirFromVendorXzUnit(downslope.x, downslope.z)
      if (dir) {
        return dir
      }
    }
  }
  return studioHorizontalSouthEastUnit(azimuthDeg)
}

function offsetAlongDownSlope(
  base: StudioPointM,
  dir: StudioHorizontalDir,
  distanceM: number,
  slopeDeg: number
): StudioPointM {
  const slopeRad = (slopeDeg * Math.PI) / 180
  const horizontal = distanceM * Math.cos(slopeRad)
  const dz = distanceM * Math.sin(slopeRad)
  return {
    x: base.x + dir.east * horizontal,
    y: base.y + dir.south * horizontal,
    z: base.z - dz,
  }
}

const VENDOR_Y_EQUAL_EPSILON_MM = 1
const NEIGHBOR_STEP_DISTANCE_FACTOR = 1.5

function studioHorizontalDistM(a: StudioPointM, b: StudioPointM): number {
  const dx = b.x - a.x
  const dy = b.y - a.y
  return Math.hypot(dx, dy)
}

function isPlausibleGridNeighborStep(
  baseM: StudioPointM,
  neighborM: StudioPointM | null,
  maxDistM: number
): neighborM is StudioPointM {
  if (!neighborM) {
    return false
  }
  return studioHorizontalDistM(baseM, neighborM) <= maxDistM
}

function subtractStudio(a: StudioPointM, b: StudioPointM): StudioPointM {
  return { x: a.x - b.x, y: a.y - b.y, z: a.z - b.z }
}

function addStudio(a: StudioPointM, b: StudioPointM): StudioPointM {
  return { x: a.x + b.x, y: a.y + b.y, z: a.z + b.z }
}

function scaleStudio(v: StudioPointM, scale: number): StudioPointM {
  return { x: v.x * scale, y: v.y * scale, z: v.z * scale }
}

function normalizeStudio(v: StudioPointM): StudioPointM | null {
  const len = Math.hypot(v.x, v.y, v.z)
  if (len < 1e-9) {
    return null
  }
  return { x: v.x / len, y: v.y / len, z: v.z / len }
}

function horizontalDirToStudioUnit(dir: StudioHorizontalDir): StudioPointM {
  return { x: dir.east, y: dir.south, z: 0 }
}

/**
 * Vendor ``base`` is the starting corner of panel 1 at ``(maxCol, minRow)``,
 * exterior to expansion along column then row steps.
 */
function moduleStartCornerFromCentre(
  anchorCentreM: StudioPointM,
  alongRowUnit: StudioPointM,
  acrossRowUnit: StudioPointM,
  layout: LayoutParams
): StudioPointM {
  const halfW = layout.moduleWidth / 2
  const halfH = layout.moduleHeight / 2
  return addStudio(
    anchorCentreM,
    addStudio(scaleStudio(alongRowUnit, -halfW), scaleStudio(acrossRowUnit, -halfH))
  )
}

/**
 * Flat-roof: vendor derives its panel-column (+X) axis from ``base → azimuth``.
 * Given horizontal row direction in studio (maps to vendor XZ), return the
 * perpendicular so vendor +X aligns with the panel row: ``(-z, x)`` in vendor XZ.
 */
function flatRoofAzimuthDirFromRowDirStudio(rowDirStudio: StudioPointM): StudioPointM {
  // studio (x,y) → vendor (x,z); azimuth vendor = (-z, x) → studio (-y, x)
  return { x: -rowDirStudio.y, y: rowDirStudio.x, z: 0 }
}

/**
 * Flat-roof field points: tangent along the panel row; azimuth is the perpendicular
 * that orients the vendor's internal +X with that row. Both are offsets from ``base``
 * (do not use neighbour centres as the placement source of truth).
 */
function buildFlatRoofLocationFromRowDirection(
  baseM: StudioPointM,
  rowDirectionUnit: StudioPointM,
  referenceLengthM: number
): { tangentM: StudioPointM; azimuthM: StudioPointM } {
  const azimuthDir = flatRoofAzimuthDirFromRowDirStudio(rowDirectionUnit)
  return {
    tangentM: {
      x: baseM.x + rowDirectionUnit.x * referenceLengthM,
      y: baseM.y + rowDirectionUnit.y * referenceLengthM,
      z: baseM.z,
    },
    azimuthM: {
      x: baseM.x + azimuthDir.x * referenceLengthM,
      y: baseM.y + azimuthDir.y * referenceLengthM,
      z: baseM.z,
    },
  }
}

/** Vendor pitched BOM: azimuth must sit downslope (lower vendor Y = height) than base. */
function applyPitchedRoofFieldLocationSlope(
  baseM: StudioPointM,
  azimuthM: StudioPointM,
  tangentM: StudioPointM,
  rowStepDir: StudioHorizontalDir,
  slopeDeg: number,
  rowStepLenM: number
): { azimuthM: StudioPointM; tangentM: StudioPointM } {
  if (slopeDeg <= 0) {
    return { azimuthM, tangentM }
  }
  const baseY = studioMetresToVendorMm(baseM).y
  if (studioMetresToVendorMm(azimuthM).y >= baseY - VENDOR_Y_EQUAL_EPSILON_MM) {
    const dx = azimuthM.x - baseM.x
    const dy = azimuthM.y - baseM.y
    const horizontalM = Math.hypot(dx, dy)
    if (horizontalM > 1e-6) {
      const slopeRad = (slopeDeg * Math.PI) / 180
      const dz = horizontalM * Math.tan(slopeRad)
      return {
        azimuthM: { x: azimuthM.x, y: azimuthM.y, z: baseM.z - dz },
        tangentM,
      }
    }
    return {
      azimuthM: offsetAlongDownSlope(baseM, rowStepDir, rowStepLenM, slopeDeg),
      tangentM,
    }
  }
  return { azimuthM, tangentM }
}

function getModuleCenterWorldM(
  grid: GridLayoutRuntime,
  moduleCoordX: number,
  moduleCoordY: number
): StudioPointM | null {
  if (typeof grid.getParamsForLayoutCalcs !== 'function' || typeof grid.positionAndRotationForCell !== 'function') {
    return null
  }
  grid.updateMatrixWorld?.()
  const layoutParams = grid.getParamsForLayoutCalcs({
    ignoreGroundClearance: true,
    flipOrientation: false,
    additionalZOffset: 0,
  })
  const result = grid.positionAndRotationForCell({
    ...layoutParams,
    moduleCoordX,
    moduleCoordY,
    applyToObject: null,
  })
  if (!result?.position) {
    return null
  }
  const pos = result.position
  if (typeof grid.localToWorld === 'function' && typeof pos.clone === 'function') {
    const world = grid.localToWorld(pos.clone())
    return { x: world.x, y: world.y, z: world.z }
  }
  return { x: pos.x, y: pos.y, z: pos.z }
}

function moduleCoordsForNormalizedCell(
  grid: GridLayoutRuntime,
  normalized: VanDerValkNormalizedGridCell[],
  ncol: number,
  nrow: number
): { moduleCoordX: number; moduleCoordY: number } | null {
  const hit = normalized.find((c) => c.ncol === ncol && c.nrow === nrow)
  if (!hit) {
    return null
  }
  const dict = grid.getCellsActiveVector2Dict?.()
  const v = dict?.[hit.cellKey]
  if (!v || !Number.isFinite(v.x) || !Number.isFinite(v.y)) {
    return null
  }
  return { moduleCoordX: v.x, moduleCoordY: v.y }
}

/** One matrix column step (``ncol`` → ``ncol + 1``). */
function singleMatrixColumnStepMetres(layout: LayoutParams): number {
  const count = Math.max(layout.modulesPerRow, 1)
  return (layout.moduleWidth * layout.modulesPerRow + layout.totalInterModuleSpaceX) / count
}

/** One matrix row step (``nrow`` → ``nrow + 1``). */
function singleMatrixRowStepMetres(layout: LayoutParams, rowPitchMm: number | undefined): number {
  if (rowPitchMm != null && rowPitchMm > 0) {
    return rowPitchMm / MM_PER_METRE
  }
  const count = Math.max(layout.modulesPerCol, 1)
  return (layout.moduleHeight * layout.modulesPerCol + layout.totalInterModuleSpaceY) / count
}

function buildLocationFromStudioPoints(
  baseM: StudioPointM,
  tangentM: StudioPointM,
  azimuthM: StudioPointM
): BuildVanDerValkFieldLocationFromGridResult {
  const base = studioMetresToVendorMm(baseM)
  const tangent = studioMetresToVendorMm(tangentM)
  const azimuth = studioMetresToVendorMm(azimuthM)
  for (const p of [base, tangent, azimuth]) {
    if (!isFinitePointMm(p) || !isWithinBoundsMm(p)) {
      return { ok: false, reason: 'invalid_field_location' }
    }
  }
  if (isCollinearMm(base, tangent, azimuth)) {
    return { ok: false, reason: 'invalid_field_location' }
  }
  return { ok: true, location: { base, azimuth, tangent } }
}

function azimuthFallbackAtCentreM(
  centreM: StudioPointM,
  rowStepDir: StudioHorizontalDir,
  colStepLenM: number,
  rowStepLenM: number,
  slopeDeg: number,
  singleRowLayout: boolean,
  colStepM: StudioPointM | null
): StudioPointM {
  if (singleRowLayout && slopeDeg > 0) {
    const slopeRad = (slopeDeg * Math.PI) / 180
    const colHorizHalfM =
      colStepM != null
        ? studioHorizontalDistM(centreM, colStepM) / 2
        : (colStepLenM * Math.cos(slopeRad)) / 2
    return {
      x: centreM.x + rowStepDir.east * colHorizHalfM,
      y: centreM.y + rowStepDir.south * colHorizHalfM,
      z: centreM.z,
    }
  }
  return offsetAlongDownSlope(centreM, rowStepDir, rowStepLenM, slopeDeg)
}

export function buildVanDerValkFieldLocationFromGrid(
  grid: ModuleGridType,
  opts?: { rowPitchMm?: number; roofPolygon?: VanDerValkRoofPolygonPoint[] }
): BuildVanDerValkFieldLocationFromGridResult {
  // Flat BOM calls without roofPolygon; pitched BOM always supplies one.
  // Do not gate on slopeDeg <= 0 — grid slope can be ~0.01° float noise on flat roofs.
  const isFlatRoofFieldLocation = !opts?.roofPolygon?.length

  const cellsActive = grid.cellsActive
  if (!Array.isArray(cellsActive) || cellsActive.length === 0) {
    return { ok: false, reason: 'no_active_layout' }
  }
  const normalized = normalizeVanDerValkGridCells(cellsActive)
  if (!normalized?.length) {
    return { ok: false, reason: 'no_active_layout' }
  }

  const gridRt = grid as GridLayoutRuntime
  const maxCol = Math.max(...normalized.map((c) => c.ncol))
  const minRow = Math.min(...normalized.map((c) => c.nrow))
  const maxRow = Math.max(...normalized.map((c) => c.nrow))
  const singleRowLayout = maxRow === minRow
  const anchorCol = maxCol
  const anchorRow = minRow

  const anchorCoords = moduleCoordsForNormalizedCell(gridRt, normalized, anchorCol, anchorRow)
  if (!anchorCoords) {
    return { ok: false, reason: 'invalid_field_location' }
  }
  const anchorCentreM = getModuleCenterWorldM(gridRt, anchorCoords.moduleCoordX, anchorCoords.moduleCoordY)
  if (!anchorCentreM) {
    return { ok: false, reason: 'invalid_field_location' }
  }

  const colStepCoords =
    anchorCol > 0 ? moduleCoordsForNormalizedCell(gridRt, normalized, anchorCol - 1, anchorRow) : null
  const rowStepCoords = moduleCoordsForNormalizedCell(gridRt, normalized, anchorCol, anchorRow + 1)

  let colStepM: StudioPointM | null = null
  if (colStepCoords) {
    colStepM = getModuleCenterWorldM(gridRt, colStepCoords.moduleCoordX, colStepCoords.moduleCoordY)
  }
  let rowStepM: StudioPointM | null = null
  if (rowStepCoords) {
    rowStepM = getModuleCenterWorldM(gridRt, rowStepCoords.moduleCoordX, rowStepCoords.moduleCoordY)
  }

  const azimuthDeg = typeof gridRt.getAzimuth === 'function' ? gridRt.getAzimuth() : 180
  const slopeDeg = typeof gridRt.getSlope === 'function' ? gridRt.getSlope() : 0
  const rowStepDir = rowStepDirForFieldLocation(azimuthDeg, slopeDeg, opts?.roofPolygon)
  const facingAzimuthDir = studioHorizontalSouthEastUnit(azimuthDeg)
  const tangentDir = tangentDirFromAzimuthDir(facingAzimuthDir)

  if (typeof gridRt.getParamsForLayoutCalcs !== 'function') {
    return { ok: false, reason: 'invalid_field_location' }
  }
  const layout = gridRt.getParamsForLayoutCalcs({
    ignoreGroundClearance: true,
    flipOrientation: false,
    additionalZOffset: 0,
  })
  const colStepLenM = singleMatrixColumnStepMetres(layout)
  const rowStepLenM = singleMatrixRowStepMetres(layout, opts?.rowPitchMm)
  const maxColNeighborDistM = colStepLenM * NEIGHBOR_STEP_DISTANCE_FACTOR
  const maxRowNeighborDistM = rowStepLenM * NEIGHBOR_STEP_DISTANCE_FACTOR

  if (!isPlausibleGridNeighborStep(anchorCentreM, colStepM, maxColNeighborDistM)) {
    colStepM = null
  }
  if (!isPlausibleGridNeighborStep(anchorCentreM, rowStepM, maxRowNeighborDistM)) {
    rowStepM = null
  }

  const colStepTowardPrevColM = colStepM != null ? subtractStudio(colStepM, anchorCentreM) : null
  const rowStepTowardNextRowM = rowStepM != null ? subtractStudio(rowStepM, anchorCentreM) : null

  // Horizontal along-row unit: next-column centre − first-column centre (or facing-perp fallback).
  const alongRowUnit =
    normalizeStudio(
      colStepTowardPrevColM != null
        ? { x: colStepTowardPrevColM.x, y: colStepTowardPrevColM.y, z: 0 }
        : { x: 0, y: 0, z: 0 }
    ) ?? horizontalDirToStudioUnit(tangentDir)

  const acrossRowUnit =
    isFlatRoofFieldLocation
      ? flatRoofAzimuthDirFromRowDirStudio(alongRowUnit)
      : normalizeStudio(
          rowStepTowardNextRowM != null
            ? { x: rowStepTowardNextRowM.x, y: rowStepTowardNextRowM.y, z: 0 }
            : { x: 0, y: 0, z: 0 }
        ) ?? horizontalDirToStudioUnit(rowStepDir)

  // Flat roof: always outer corner of the first module. Pitched multi-column keeps centre
  // (partner-calibrated) and only corners the single-column case.
  const baseM =
    !isFlatRoofFieldLocation && slopeDeg > 0 && anchorCol > 0
      ? anchorCentreM
      : moduleStartCornerFromCentre(anchorCentreM, alongRowUnit, acrossRowUnit, layout)

  let tangentM: StudioPointM
  let azimuthM: StudioPointM

  if (isFlatRoofFieldLocation) {
    // Flat roof: vendor +X comes from base→azimuth; choose azimuth ⊥ to the panel row.
    // Tangent still describes the row but is not the placement source of truth.
    const referenceLengthM =
      colStepTowardPrevColM != null
        ? Math.hypot(colStepTowardPrevColM.x, colStepTowardPrevColM.y)
        : colStepLenM
    const flat = buildFlatRoofLocationFromRowDirection(baseM, alongRowUnit, referenceLengthM)
    tangentM = flat.tangentM
    azimuthM = flat.azimuthM
    if (slopeDeg > 0) {
      const pitchedAdjusted = applyPitchedRoofFieldLocationSlope(
        baseM,
        azimuthM,
        tangentM,
        rowStepDir,
        slopeDeg,
        rowStepLenM
      )
      azimuthM = pitchedAdjusted.azimuthM
      tangentM = pitchedAdjusted.tangentM
    }
  } else {
    // Pitched: tangent = along-row (toward ncol - 1); azimuth = next row / downslope.
    tangentM = colStepM ?? addStudio(baseM, scaleStudio(alongRowUnit, colStepLenM))
    azimuthM =
      rowStepM ??
      azimuthFallbackAtCentreM(
        baseM,
        rowStepDir,
        colStepLenM,
        rowStepLenM,
        slopeDeg,
        singleRowLayout,
        colStepM
      )
    const pitchedAdjusted = applyPitchedRoofFieldLocationSlope(
      baseM,
      azimuthM,
      tangentM,
      rowStepDir,
      slopeDeg,
      rowStepLenM
    )
    azimuthM = pitchedAdjusted.azimuthM
    tangentM = pitchedAdjusted.tangentM
  }

  const built = buildLocationFromStudioPoints(baseM, tangentM, azimuthM)
  if (!built.ok) {
    return built
  }

  let location = built.location
  if (slopeDeg > 0 && opts?.roofPolygon?.length) {
    const onPlane = buildVanDerValkFieldLocationOnRoofPlane(location, opts.roofPolygon)
    if (!onPlane) {
      return { ok: false, reason: 'invalid_field_location' }
    }
    if (isCollinearMm(onPlane.base, onPlane.tangent, onPlane.azimuth)) {
      return { ok: false, reason: 'invalid_field_location' }
    }
    location = onPlane
  }

  return { ok: true, location }
}
